/* eslint-disable no-unused-vars */
/* eslint-disable consistent-return */
/* eslint-disable class-methods-use-this */
import log from 'loglevel';
import store from 'features/store';
import {
  resetLoadingUserState,
  setAuth0AuthenticatedStatus,
} from 'features/Authentication/redux/authActions';

// BFF-based authentication - no client-side Auth0 SDK needed
class Auth {
  constructor(config) {
    this.bffBaseUrl = config.REACT_APP_UPLOAD_API_BASE_URL || window.location.origin;
    this.redirectUri = config.REACT_APP_AUTH_REDIRECT_URI;
    this.googleInitializeRedirectUri = config.REACT_APP_GOOGLE_AUTH_REDIRECT_URI;
  }

  authPopup = null;
  initializeAccountWithGoogle = (callback) => {
    const selectedLanguage = localStorage.getItem('i18nextLng');
    const returnUrl = this.googleInitializeRedirectUri || '/';
    const returnUrlWithCallback = `${returnUrl}${
      returnUrl.includes('?') ? '&' : '?'
    }auth_callback=true`;

    // Redirect to BFF login endpoint with Google connection
    window.location.href = `${
      this.bffBaseUrl
    }/event/api/auth/login?connection=google-oauth2&ui_locales=${selectedLanguage}&returnUrl=${encodeURIComponent(
      returnUrlWithCallback
    )}`;
  };

  openPopup = (url, loginName) => {
    const width = 500;
    const height = 600;

    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const features = `
    width=${width},
    height=${height},
    left=${left},
    top=${top}
  `;

    // MUST open immediately (prevents popup blocking)
    const popup = window.open('', loginName, features);

    if (!popup) {
      // Popup blocked → fallback to redirect
      window.location.href = url;
      return null;
    }

    popup.location.href = url;
    return popup;
  };

  googleAuthentication = (callback) => {
    const selectedLanguage = localStorage.getItem('i18nextLng');
    // const returnUrl = this.redirectUri || '/';
    // const returnUrlWithCallback = `${returnUrl}${
    //   returnUrl.includes('?') ? '&' : '?'
    // }auth_callback=true`;

    // Redirect to BFF login endpoint with Google connection
    // Use popup callback to detect login success and close popup
    const returnUrl = `${window.location.origin}/popup-callback`;

    const loginUrl = `${
      this.bffBaseUrl
    }/event/api/auth/login?connection=google-oauth2&ui_locales=${selectedLanguage}&returnUrl=${encodeURIComponent(
      returnUrl
    )}`;

    this.authPopup = this.openPopup(loginUrl, 'google-login');
  };

  accountAuthentication = (callback) => {
    const selectedLanguage = localStorage.getItem('i18nextLng');
    //const returnUrl = this.redirectUri || '/';
    // Use popup callback to detect login success and close popup
    const returnUrl = `${window.location.origin}/popup-callback`;
    const loginUrl = `${
      this.bffBaseUrl
    }/event/api/auth/login?ui_locales=${selectedLanguage}&returnUrl=${encodeURIComponent(
      returnUrl
    )}`;

    this.authPopup = this.openPopup(loginUrl, 'auth0-login');
  };

  // Helper to parse claims array into user object
  parseClaimsToUser = (claims) => {
    if (!claims || !Array.isArray(claims)) {
      return null;
    }

    const getClaim = (claimType) => {
      const claim = claims.find(
        (c) =>
          c.type === claimType ||
          c.type.endsWith(`/${claimType}`) ||
          c.type.endsWith(`/claims/${claimType}`)
      );
      return claim ? claim.value : null;
    };

    return {
      email: getClaim('emailaddress') || getClaim('email'),
      name: getClaim('name'),
      userId: getClaim('nameidentifier') || getClaim('sub'),
      picture: getClaim('picture'),
      given_name: getClaim('givenname'),
      family_name: getClaim('surname'),
    };
  };

  handleAuthentication = async () => {
    try {
      // Check if user is authenticated via BFF
      const response = await fetch(`${this.bffBaseUrl}/event/api/auth/me`, {
        credentials: 'include', // Include cookies
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Authentication failed');
      }

      const claims = await response.json();
      log.debug(`Received claims from BFF`);

      // Parse claims array into user object
      const userData = this.parseClaimsToUser(claims);

      if (!userData || !userData.email) {
        log.error('Invalid user data or missing email');
        throw new Error('Not authenticated - missing user information');
      }

      log.debug(`Success handleAuthentication`);

      // BFF provides clean user data - no tokens needed
      store.dispatch(setAuth0AuthenticatedStatus(true));
      return {
        email: userData.email,
        name: userData.name,
        userId: userData.userId,
        picture: userData.picture,
      };
    } catch (err) {
      log.error(`Error handleAuthentication ${err.message}`);
      throw err;
    }
  };

  handleRefreshToken = async () => {
    try {
      // BFF handles refresh token automatically via cookies
      // Just check if user is still authenticated
      const response = await fetch(`${this.bffBaseUrl}/event/api/auth/refresh`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Session expired');
      }

      const refreshData = await response.json();

      if (refreshData.refreshed) {
        log.info(`Success handleRefreshToken-authenticated`);

        // Get updated user info
        const userResponse = await fetch(`${this.bffBaseUrl}/event/api/auth/me`, {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        const claims = await userResponse.json();
        const userData = this.parseClaimsToUser(claims);

        // BFF provides clean user data - no tokens needed
        store.dispatch(setAuth0AuthenticatedStatus(true));
        return {
          email: userData.email,
          name: userData.name,
          userId: userData.userId,
          picture: userData.picture,
        };
      }
    } catch (err) {
      log.error(`Error handleRefreshToken ${err.message}`);
      store.dispatch(resetLoadingUserState());
      throw err;
    }
  };

  signOut = () => {
    window.location.href = `${
      this.bffBaseUrl
    }/event/api/auth/logout?federated=true&returnUrl=${encodeURIComponent(
      `${window.location.origin}/login`
    )}`;
  };

  // Helper method to check authentication status
  isAuthenticated = async () => {
    try {
      const response = await fetch(`${this.bffBaseUrl}/event/api/auth/me`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        return false;
      }

      const claims = await response.json();
      const userData = this.parseClaimsToUser(claims);
      return !!(userData && userData.email);
    } catch (err) {
      log.error(`Error checking authentication: ${err.message}`);
      return false;
    }
  };

  // Get current user data
  getUser = async () => {
    try {
      const response = await fetch(`${this.bffBaseUrl}/event/api/auth/me`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        return null;
      }

      const claims = await response.json();
      const userData = this.parseClaimsToUser(claims);
      return userData && userData.email ? userData : null;
    } catch (err) {
      log.error(`Error getting user: ${err.message}`);
      return null;
    }
  };

  static authInstance = null;
  static CreateInstance(config) {
    this.authInstance = new Auth(config);
  }

  static GetInstance() {
    return this.authInstance;
  }
}

export default Auth;
